import { MatDialog } from '@angular/material/dialog';
import { Injectable } from '@angular/core';
import { ApiService } from 'app/shared/apis/api.service';
import { BaseService } from 'app/core/services/base.service';
import { AllocationRole, AllocationModel } from 'app/models/allocation.model';

export interface UpsertAllocationBody {
    service_id?: number | null;
    company_id: number;
    vehicle_id?: number | null;
    shift_id?: number | null;
    users: { user_id: number; role?: AllocationRole; shift_id?: number | null }[];
    started_at?: Date | string | null;
    append_to_open?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AllocationService extends BaseService<AllocationModel> {
    constructor(api: ApiService, _matDialog: MatDialog) {
        super(api, _matDialog, 'allocations', 'allocation_id');
    }

    upsert(body: UpsertAllocationBody) {
        const $request = this.api.postSub<AllocationModel>(`${this.endpoint}/create`, body);
        return this.withPromise($request);
    }

    getByService(serviceId: number) {
        const $request = this.api.getSub<AllocationModel>(`${this.endpoint}/by-service/${serviceId}`);
        return this.withPromise($request);
    }

    getOpenByVehicle(vehicleId: number) {
        const $request = this.api.getSub<AllocationModel>(`${this.endpoint}/by-vehicle/${vehicleId}/open`);
        return this.withPromise($request);
    }

    getOpenByShift(shiftId: number) {
        const $request = this.api.getSub<AllocationModel>(`${this.endpoint}/by-shift/${shiftId}/open`);
        return this.withPromise($request);
    }

    getOpenByUser(userId: number) {
        const $request = this.api.getSub<AllocationModel[]>(`${this.endpoint}/by-user/${userId}/open`);
        return this.withPromise($request);
    }

    getOpenByCompany(companyId: number) {
        const $request = this.api.getSub<AllocationModel[]>(`${this.endpoint}/by-company/${companyId}/open`);
        return this.withPromise($request);
    }

    getPendingDivergences(companyId: number) {
        const $request = this.api.getSub<{ allocation_ids: number[] }>(
            `${this.endpoint}/by-company/${companyId}/pending-divergences`,
        );
        return this.withPromise($request);
    }

    getByCompanyDay(companyId: number, date: string, timezone?: string) {
        const params: Record<string, string> = { date };
        if (timezone) params['timezone'] = timezone;
        const $request = this.api.getSub<AllocationModel[]>(`${this.endpoint}/by-company/${companyId}/day`, params);
        return this.withPromise($request);
    }

    /** Alocações do dia dos médicos reguladores — cross-company (sem escopo de empresa). */
    getByRegulatorsDay(date: string, timezone?: string) {
        const params: Record<string, string> = { date };
        if (timezone) params['timezone'] = timezone;
        const $request = this.api.getSub<AllocationModel[]>(`${this.endpoint}/by-regulators/day`, params);
        return this.withPromise($request);
    }

    /** Lista os médicos reguladores da plataforma — cross-company. */
    getRegulatorsList() {
        const $request = this.api.getSub<{ user_id: number; name: string }[]>(`${this.endpoint}/by-regulators/list`);
        return this.withPromise($request);
    }

    /** Relatório mensal dos plantões finalizados dos médicos reguladores — cross-company. */
    getRegulatorsClosedReport(month: string, timezone?: string) {
        const params: Record<string, string> = { month };
        if (timezone) params['timezone'] = timezone;
        const $request = this.api.getSub<RegulatorClosedReportRow[]>(
            `${this.endpoint}/by-regulators/closed-report`,
            params,
        );
        return this.withPromise($request);
    }

    getByCompanyMonth(companyId: number, month: string, timezone?: string) {
        const params: Record<string, string> = { month };
        if (timezone) params['timezone'] = timezone;
        const $request = this.api.getSub<AllocationModel[]>(`${this.endpoint}/by-company/${companyId}/month`, params);
        return this.withPromise($request);
    }

    getClosedReport(companyId: number, month: string, timezone?: string) {
        const params: Record<string, string> = { month };
        if (timezone) params['timezone'] = timezone;
        const $request = this.api.getSub<ClosedAllocationReportRow[]>(
            `${this.endpoint}/by-company/${companyId}/closed-report`,
            params,
        );
        return this.withPromise($request);
    }

    close(serviceId: number) {
        const $request = this.api.putSub<AllocationModel>(`${this.endpoint}/by-service/${serviceId}/close`, {});
        return this.withPromise($request);
    }

    closeById(allocationId: number, endedAt?: string) {
        const $request = this.api.putSub<AllocationModel>(`${this.endpoint}/${allocationId}/close`, {
            ended_at: endedAt,
        });
        return this.withPromise($request);
    }

    deleteById(allocationId: number) {
        const $request = this.api.deleteSub<{ ok: boolean }>(`${this.endpoint}/${allocationId}`);
        return this.withPromise($request);
    }

    removeUser(allocationId: number, userId: number) {
        const $request = this.api.deleteSub(`${this.endpoint}/${allocationId}/users/${userId}`);
        return this.withPromise($request);
    }

    bindVehicle(allocationId: number, vehicleId: number) {
        const $request = this.api.putSub<AllocationModel>(`${this.endpoint}/${allocationId}/bind-vehicle`, {
            vehicle_id: vehicleId,
        });
        return this.withPromise($request);
    }

    updateSchedule(allocationId: number, started_at: string, ended_at?: string | null) {
        const $request = this.api.putSub<AllocationModel>(`${this.endpoint}/${allocationId}/schedule`, {
            started_at,
            ended_at,
        });
        return this.withPromise($request);
    }

    getEligibleUsers(companyId: number, serviceStart?: Date | string | null) {
        const params: Record<string, unknown> = { company_id: companyId };
        if (serviceStart) {
            params['service_start'] =
                serviceStart instanceof Date ? serviceStart.toISOString() : serviceStart;
        }
        const $request = this.api.getSub<EligibleUsersResponse>(`${this.endpoint}/eligible-users`, params);
        return this.withPromise($request);
    }
}

export interface EligibleUser {
    user_id: number;
    name: string | null;
    profile_type: string | null;
    is_available: boolean;
    eligible: boolean;
    planned_shift_id: number | null;
    on_planned_schedule: boolean;
    outside_planned_schedule: boolean;
    is_remote_shift: boolean;
}

export interface EligibleUsersResponse {
    users: EligibleUser[];
}

export interface ClosedAllocationReportRow {
    name: string;
    role: string;
    group: string;
    started_at: string | Date;
    ended_at: string | Date;
    hours: number;
}

export interface RegulatorClosedReportRow {
    name: string;
    company: string;
    started_at: string | Date;
    ended_at: string | Date;
    hours: number;
}
